/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable  @typescript-eslint/no-unused-vars */
import {Injectable} from '@angular/core';
import {AlertModalService} from '../../../../common/component/modal';
import {EnlargementUtilService} from './enlargement-util.service';
import {FeaturesApplyInsertService} from '../../../services/features-apply-insert.service';
import {FeatureApplyUpdateService} from '../../../services/feature-apply-update.service';
import {SpatialSequenceService} from '../../../../common/service/spatial-sequence.service';
import {PlanModelService} from '../../../../common/service/plan-model.service';
import {SpatialUtilService} from '../../../services/spatial-util.service';
import {EsriModuleService} from '../../../esri-nlr/services/esri-module.service';
import {catchError, concatMap, of, tap} from 'rxjs';
import {FeatureApplyDeleteService} from '../../../services/feature-apply-delete.service';
import {switchMap} from 'rxjs/operators';
import {forkJoinDefaultIfEmpty} from '../../../../common/observable.utils';

@Injectable({
    providedIn: 'root'
})
export class EnlargementClippingService {
    constructor(
        private alertModalService: AlertModalService,
        private enlargementUtilService: EnlargementUtilService,
        private planModelService: PlanModelService,
        private featuresApplyDeleteService: FeatureApplyDeleteService,
        private featuresApplyInsertService: FeaturesApplyInsertService,
        private featuresApplyUpdateService: FeatureApplyUpdateService,
        private spatialSequenceService: SpatialSequenceService,
        private spatialUtilService: SpatialUtilService,
        private esriModuleService: EsriModuleService
    ) {}

    title = 'EnlargementClippingService';
    MASKED = 1;
    UNMASKED = 2;
    CREATE_CLIPPING = 0;
    EDIT_CLIPPING = 1;

    /**
     * Clears the current clipping and updates the visibility of all features.
     */
    clearAndDeleteClipping(currentClippingFeatures: any[], enlargementFeature: any) {
        // Update feature visibility
        const visibility = this.updateFeatureVisibility(undefined, enlargementFeature.attributes.id).pipe(
            tap(() => {
                console.debug('clearAndDeleteClipping - Successfully updated feature visibility.');
            }),
            catchError(error => {
                console.error(`${this.title} : clearAndDeleteClipping - Error updating feature visibility.`, error);
                return of();
            })
        );

        const deleteFeatures = this.featuresApplyDeleteService
            .commitFeaturesDeletion(this.spatialUtilService.getClippingMapLayer(), currentClippingFeatures, false)
            .pipe(
                switchMap((deletedPolygons: any[]) => {
                    if (deletedPolygons && deletedPolygons.length > 0) {
                        console.debug('Clipping polygon has been successfully deleted!');

                        // Remove clipping ID from enlargement
                        enlargementFeature.attributes.clippingid = null;
                        return this.enlargementUtilService.updateEnlargementFeatures([enlargementFeature]).pipe(
                            tap(() => {
                                console.debug('clearAndDeleteClipping - Successfully updated enlargement feature.');
                            }),
                            catchError(error => {
                                console.error(`${this.title} : clearAndDeleteClipping - Error updating enlargement feature.`, error);
                                return of();
                            })
                        );
                    } else {
                        console.debug('Error deleting clipping polygon!');
                        this.alertModalService.createAlertMessage('Error occurred deleting clipping polygon feature.');
                        return of();
                    }
                })
            );
        return forkJoinDefaultIfEmpty([visibility, deleteFeatures]).pipe(
            tap(() => {
                console.debug(`${this.title} : clearAndDeleteClipping - All operations completed successfully.`);
            }),
            catchError(error => {
                console.error(`${this.title} : clearAndDeleteClipping - Error occurred during operations.`, error);
                return of();
            })
        );
    }

    /**
     * Creates or updates a clipping feature.
     */
    createClipping(clippingHoleGeometry: any, clippingUnmaskedGeometry: any, enlargementFeature: any, currentClippingFeatures: any[]) {
        const polygonAttributes: any = {
            guid: this.planModelService.getGuid(),
            type: this.MASKED,
            enlargementid: enlargementFeature.attributes.id,
            scale: enlargementFeature.attributes.scale,
            fullpage: enlargementFeature.attributes.fullpage
        };

        if (currentClippingFeatures.length === 0) {
            // create a new clipping feature and set visibility of annotations and angles
            const clippingIds = this.spatialSequenceService.getClippingSequence(2);

            let polygonGraphic = new this.esriModuleService.Graphic(clippingHoleGeometry);
            polygonAttributes.id = clippingIds[0];
            polygonGraphic.attributes = polygonAttributes;
            this.createClippingFeature(polygonGraphic);

            // Store unmasked clipping (backward compatibility)
            if (clippingUnmaskedGeometry) {
                polygonGraphic = new this.esriModuleService.Graphic(clippingUnmaskedGeometry);
                polygonGraphic.attributes = Object.assign(structuredClone(polygonAttributes), {id: clippingIds[1], type: this.UNMASKED});
                this.createClippingFeature(polygonGraphic);
            }

            enlargementFeature.attributes.clippingid = clippingIds[0];

            return this.enlargementUtilService.updateEnlargementFeatures([enlargementFeature]).pipe(
                concatMap(() =>
                    this.updateFeatureVisibility(clippingHoleGeometry, enlargementFeature.attributes.id, this.CREATE_CLIPPING).pipe(
                        tap(() => {
                            console.debug('createClipping - Successfully updated feature visibility.');
                        }),
                        catchError(error => {
                            console.error(`${this.title} : createClipping - Error updating feature visibility.`, error);
                            return of([]);
                        })
                    )
                )
            );
        } else {
            // existing clipping - update the clipping feature and set visibility of annotations and angles
            return this.enlargementUtilService.updateEnlargementFeatures([enlargementFeature]).pipe(
                concatMap(() => {
                    currentClippingFeatures.forEach(clippingFeature => {
                        clippingFeature.geometry = clippingFeature.attributes.type === this.MASKED ? clippingHoleGeometry : clippingUnmaskedGeometry;
                    });
                    return this.featuresApplyUpdateService
                        .updateFeaturesByType(this.spatialUtilService.getClippingMapLayer(), currentClippingFeatures)
                        .pipe(
                            tap(() => {
                                console.debug('Clipping polygon has been successfully updated!');
                            })
                        );
                }),
                concatMap(() =>
                    this.updateFeatureVisibility(clippingHoleGeometry, enlargementFeature.attributes.id, this.EDIT_CLIPPING).pipe(
                        tap(() => {
                            console.debug('createClipping - Successfully updated feature visibility.');
                        }),
                        catchError(error => {
                            console.error(`${this.title} : createClipping - Error updating feature visibility.`, error);
                            return of([]);
                        })
                    )
                ),
                concatMap(() => {
                    const clippingFeatures = this.spatialUtilService
                        .getClippingGraphics()
                        .filter(f => f.attributes.enlargementid === enlargementFeature.attributes.id);

                    if (clippingFeatures && clippingFeatures.length > 0) {
                        const foundUnmaskedFeature = clippingFeatures.find(item => item.attributes.type === this.UNMASKED);
                        //backward compatibility
                        if (foundUnmaskedFeature) {
                            foundUnmaskedFeature.geometry = clippingUnmaskedGeometry;
                            return this.featuresApplyUpdateService
                                .updateFeaturesByType(this.spatialUtilService.getClippingMapLayer(), [foundUnmaskedFeature])
                                .pipe(
                                    tap(() => {
                                        console.debug('Clipping unmasked polygon has been successfully updated!');
                                    })
                                );
                        }
                    }
                    return of([]);
                })
            );
        }
    }
    /**
     * Updates the visibility of annotation and angle features for the given clipping geometry. The feature is shown or hidden based on whether a
     * clipping is present and whether the feature is outside of that clipping.
     */
    updateFeatureVisibility(clippingHoleGeometry: any, enlargementId: string, mode?: number) {
        // set visibility for annotation features
        const updateAnnotations = this.findFeaturesForEnlargement(this.spatialUtilService.getFeatureLayer('annotation'), enlargementId).pipe(
            concatMap((annotations: any[]) => {
                if (annotations.length > 0) {
                    // update the visibility flags
                    if (mode === this.CREATE_CLIPPING) {
                        annotations.forEach(annotation => {
                            if (annotation.attributes.featuretype !== 'enlargement') {
                                this.setVisibility(annotation, clippingHoleGeometry);
                            }
                        });
                    }
                    return this.updateFeatures(this.spatialUtilService.getFeatureLayer('annotation'), annotations);
                }
                return of([]);
            })
        );
        // set visibility for angle features
        const updateAngles = this.findFeaturesForEnlargement(this.spatialUtilService.getFeatureLayer('angle'), enlargementId).pipe(
            switchMap((angles: any[]) => {
                if (angles.length > 0) {
                    if (mode === this.CREATE_CLIPPING) {
                        angles.forEach(angle => {
                            this.setVisibility(angle, clippingHoleGeometry);
                        });
                    }
                    return this.updateFeatures(this.spatialUtilService.getFeatureLayer('angle'), angles);
                }
                return of([]); // If no angles, return empty observable
            })
        );

        return forkJoinDefaultIfEmpty([updateAnnotations, updateAngles]).pipe(
            tap(() => {
                console.debug(`${this.title} : updateFeatureVisibility - All features successfully updated.`);
            }),
            catchError(error => {
                console.error(`${this.title} : updateFeatureVisibility - Error updating features.`, error);
                return of([]);
            })
        );
    }
    /**
     * Creates the clipping feature.
     */
    createClippingFeature(clippingFeature) {
        this.featuresApplyInsertService.insertFeaturesByType(this.spatialUtilService.getClippingMapLayer(), [clippingFeature]).subscribe(meta => {
            const createdPolygons = meta;
            if (createdPolygons && createdPolygons.length > 0) {
                console.debug('Clipping polygon has been successfully created!');
            } else {
                console.debug('Error creating clipping polygon!');
                this.alertModalService.createAlertMessage('Error occurred creating clipping polygon feature.');
            }
        });
    }

    /**
     * Find the features in the given layer for an enlargement.
     */
    findFeaturesForEnlargement(layer, enlargementId) {
        return of(
            this.spatialUtilService.getGraphics(layer).filter(f => {
                return f.attributes.enlargementid === enlargementId;
            })
        );
    }

    /**
     * Updates the list of features for the given layer.
     */
    updateFeatures(layer: any, data: any[]) {
        if (data && data.length > 0) {
            return this.featuresApplyUpdateService.updateFeaturesByType(layer, data).pipe(
                tap((updatedFeatures: any[]) => {
                    if (updatedFeatures && updatedFeatures.length > 0) {
                        console.debug(`${this.title} : updateFeatures - Features have been successfully updated!`);
                    } else {
                        console.debug(`${this.title} : updateFeatures - Error updating features`);
                    }
                })
            );
        }
        return of({});
    }

    /**
     * Sets the visibility for the given feature using the clipping geometry (if applicable).
     */
    setVisibility(feature, clippingHoleGeometry) {
        if (clippingHoleGeometry && clippingHoleGeometry.contains(feature.geometry)) {
            feature.attributes.visible = null;
            feature.attributes.hiddenonenlargements = `[${feature.attributes.enlargementid}]`;
        }
    }
}
